import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { colors, fontSize } from "style";
import { useVideoPresentation } from "hooks";
import { Spinner } from "components/Loader";
import { PrimaryButton } from "components/Buttons";
import Card from "components/Card";
import { Col, Row } from "components/Containers";
import { Editor, FileUpload, Select, SIZE, TextInput } from "components/Form";
import Icon, { ICON_TYPE } from "components/Icon";
import { convertFromPlainText, getPlainText } from "components/Form/Editor";
import { INTERVIEW_QUESTION_TIME_LIMIT_OPTIONS } from "constants";
import { Banner } from "components/Banner";
import { BANNER_TYPE } from "constants";

/** SortableItem Component
 *
 * @param {Object}    question
 * @param {function}  handleVideoUpload
 * @param {Boolean}   uploading
 * @param {function}  setQuestions
 * @param {Array}     questions
 * @param {Boolean}   isInterviewDisabled
 */
const SortableItem = ({ question, handleVideoUpload, uploading, setQuestions, questions, isInterviewDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: question.clientId,
    disabled: !question.persisted || isInterviewDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card css={styles.card}>
        <Row css={styles.row}>
          <Col md={1} css={styles.handle_col}>
            <span
              {...(question.persisted ? listeners : {})}
              {...(question.persisted ? attributes : {})}
              css={[styles.drag_handle, !question.persisted && styles.drag_disabled]}
            >
              <FormattedMessage id="VideoPresentation.InterviewQuestionsList.DragHandle" />
            </span>

            {!question.persisted && !isInterviewDisabled && (
              <Icon
                type={ICON_TYPE.trash}
                css={styles.delete_icon}
                onClick={() => {
                  const updated = questions.filter((item) => item.clientId !== question.clientId);
                  setQuestions(updated.map((q, index) => ({ ...q, position: index + 1 })));
                }}
              />
            )}
          </Col>

          <Col md={3} css={styles.video_col}>
            {question.video ? (
              <video src={question.video} controls css={styles.video} />
            ) : (
              <div css={styles.no_video_box}>
                {uploading ? (
                  <Spinner />
                ) : (
                  <small>
                    <FormattedMessage id="VideoPresentation.InterviewQuestionsList.NoVideoUploaded" />
                  </small>
                )}
              </div>
            )}
            <FileUpload
              accept="video/*"
              uploadMessageId={`VideoPresentation.InterviewQuestionsList.${
                question.persisted ? "FileUpload" : "SaveBeforeUpload"
              }.Title`}
              onChange={(name, file) => handleVideoUpload(question.id, file)}
              disabled={!question.persisted || isInterviewDisabled}
              showFilePreview={false}
            />
          </Col>

          <Col md={8} css={styles.content_col}>
            <div css={styles.title_row}>
              <TextInput
                name="title"
                value={question.title}
                onChange={(name, value) => {
                  setQuestions((prev) =>
                    prev.map((item) => (item.clientId === question.clientId ? { ...item, title: value } : item))
                  );
                }}
                css={[styles.title_input, !question.title?.trim() && styles.input_error]}
                disabled={isInterviewDisabled}
              />

              <Select
                name="timeLimitSeconds"
                value={question?.timeLimitSeconds}
                onChange={(name, value) => {
                  setQuestions((prev) =>
                    prev.map((item) =>
                      item.clientId === question.clientId ? { ...item, timeLimitSeconds: value } : item
                    )
                  );
                }}
                options={INTERVIEW_QUESTION_TIME_LIMIT_OPTIONS}
                labelKey="label"
                valueKey="value"
                css={styles.time_limit_select}
                disabled={isInterviewDisabled}
              />
            </div>
            <Editor
              name="descriptionJson"
              height={SIZE.medium}
              value={question.descriptionJson ?? convertFromPlainText(question.description)}
              onChange={(name, value) => {
                setQuestions((prev) =>
                  prev.map((item) =>
                    item.clientId === question.clientId
                      ? {
                          ...item,
                          descriptionJson: value,
                          description: getPlainText(value),
                        }
                      : item
                  )
                );
              }}
              css={!question.description?.trim() && styles.input_error}
              showFormattingOptions
              readOnly={isInterviewDisabled}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

/**
 * InterviewQuestionsList
 *
 * @param {Object}  interviewData
 * @param {Boolean} loading
 */
const InterviewQuestionsList = ({ interviewData, loading, ...props }) => {
  const isInterviewDisabled = interviewData?.responsesCount > 0;
  const [questions, setQuestions] = useState([]);
  const {
    handleInterviewQuestionsUpdate,
    handleInterviewQuestionVideoUpload,
    handleInterviewIntroVideoUpload,
    updatingQuestions,
    uploadingQuestionVideo,
    uploadingIntroVideo,
  } = useVideoPresentation();

  const handleVideoUpload = (questionId, file) => {
    handleInterviewQuestionVideoUpload(interviewData.uuid, questionId, file);
  };

  const handleIntroVideoUpload = (_n, file) => {
    handleInterviewIntroVideoUpload(interviewData.uuid, file);
  };

  const handleSaveConfiguration = () => {
    handleInterviewQuestionsUpdate({ interviewId: interviewData.uuid, questions });
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: null,
      clientId: `temp-${Date.now()}`,
      title: "",
      description: "",
      descriptionJson: convertFromPlainText(""),
      video: null,
      persisted: false,
      position: questions.length + 1,
      timeLimitSeconds: 120,
    };

    const updated = [...questions, newQuestion];
    setQuestions(updated);
  };

  const normalizePositions = (list) =>
    list.map((q, index) => ({
      ...q,
      position: index + 1,
    }));

  const hasInvalidQuestions = questions.some((q) => !q.title?.trim() || !q.description?.trim());

  useEffect(() => {
    if (interviewData && interviewData.questions) {
      const withIds = normalizePositions(
        interviewData.questions.map((q) => ({
          ...q,
          id: q.id,
          clientId: q.id,
          persisted: true,
          position: q.position,
        }))
      );

      setQuestions(withIds);
    }
  }, [interviewData]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <Container fluid css={styles.container} {...props}>
      <Row css={styles.header}>
        <IntroVideoMedia
          video={interviewData?.introVideoUrl}
          onUpload={handleIntroVideoUpload}
          uploading={uploadingIntroVideo}
          isInterviewDisabled={isInterviewDisabled}
        />

        {isInterviewDisabled && (
          <Banner
            keyName="interview-configuration-locked"
            isClosable={false}
            type={BANNER_TYPE.warning}
            css={styles.locked_banner}
          >
            <FormattedMessage id="VideoPresentation.InterviewQuestionsList.ConfigurationLocked" />
          </Banner>
        )}
      </Row>

      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;

          const oldIndex = questions.findIndex((q) => q.clientId === active.id);
          const newIndex = questions.findIndex((q) => q.clientId === over.id);

          const newQuestions = normalizePositions(arrayMove(questions, oldIndex, newIndex));

          setQuestions(newQuestions);

          handleInterviewQuestionsUpdate({ interviewId: interviewData.uuid, questions: newQuestions });
        }}
      >
        <SortableContext items={questions.map((q) => q.clientId)} strategy={verticalListSortingStrategy}>
          {questions.map((q) => (
            <SortableItem
              key={q.clientId}
              question={q}
              handleVideoUpload={handleVideoUpload}
              uploading={uploadingQuestionVideo}
              setQuestions={setQuestions}
              questions={questions}
              isInterviewDisabled={isInterviewDisabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      {!isInterviewDisabled && (
        <div css={styles.add_new_question} onClick={handleAddQuestion}>
          <FormattedMessage id="VideoPresentation.InterviewQuestionsList.AddNewQuestion" />
        </div>
      )}

      <div css={styles.footer}>
        <PrimaryButton
          onClick={handleSaveConfiguration}
          disabled={updatingQuestions || hasInvalidQuestions || isInterviewDisabled}
        >
          <FormattedMessage id="VideoPresentation.InterviewQuestionsList.SaveConfiguration" />
        </PrimaryButton>
      </div>
    </Container>
  );
};

/**
 * IntroVideoMedia
 *
 * @param {File}      video
 * @param {Function}  onUpload
 * @param {Boolean}   uploading
 * @param {Boolean}   isInterviewDisabled
 */
const IntroVideoMedia = ({ video, onUpload, uploading, isInterviewDisabled }) => {
  return (
    <div css={styles.intro_media}>
      {video ? (
        <>
          <video src={video} controls css={styles.intro_video} />
        </>
      ) : (
        <div css={styles.intro_placeholder}>
          {uploading ? <Spinner /> : <FormattedMessage id="VideoPresentation.IntroVideo.NoVideoUploaded" />}
        </div>
      )}

      <div css={styles.intro_upload}>
        {video && (
          <span css={styles.intro_upload_text}>
            <Icon type={ICON_TYPE.trash} css={styles.delete_icon} onClick={() => onUpload(null, null)} />
          </span>
        )}

        <FileUpload
          accept="video/*"
          uploadMessageId="VideoPresentation.IntroVideo.FileUpload.Title"
          onChange={onUpload}
          showFilePreview={false}
          disabled={isInterviewDisabled}
        />
      </div>
    </div>
  );
};

const styles = {
  container: css`
    padding: 24px;
    background: ${colors.grayAnatomyLight8};
    min-height: 100vh;
  `,
  card: css`
    margin-bottom: 24px;
    padding: 16px;
    border-radius: 12px;
    background: ${colors.white};
    box-shadow: 0 4px 12px rgba(38, 34, 107, 0.08);
    border: 1px solid ${colors.grayAnatomyLight3};
  `,
  row: css`
    display: flex;
    align-items: stretch;
    gap: 16px;
  `,
  handle_col: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    color: ${colors.grayAnatomyBase};
  `,
  drag_handle: css`
    cursor: grab;
    font-size: 20px;
    user-select: none;
    color: ${colors.purpleRainBase};

    &:hover {
      color: ${colors.purpleRainDark1};
    }
  `,
  delete_icon: css`
    font-size: 18px;
    cursor: pointer;
    color: ${colors.red};
    transition: 0.15s ease;

    &:hover {
      transform: scale(1.15);
      color: ${colors.darkRed};
    }
  `,
  video_col: css`
    width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  `,
  video: css`
    width: 100%;
    border-radius: 8px;
    background: ${colors.black};
  `,
  no_video_box: css`
    width: 100%;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${colors.grayAnatomyLight2};
    border-radius: 8px;
    background: ${colors.grayAnatomyLight5};
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyBase2};
    text-align: center;
  `,
  content_col: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  footer: css`
    text-align: right;
    margin-top: 16px;
  `,
  add_new_question: css`
    width: 100%;
    padding: 18px;
    margin-top: 20px;
    border: 2px dashed ${colors.purpleRainLight2};
    border-radius: 12px;

    color: ${colors.purpleRainBase};
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    background: ${colors.purpleRainLight5};

    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: ${colors.purpleRainLight3};
      border-color: ${colors.purpleRainBase};
    }
  `,
  intro_media: css`
    width: 260px;
    margin-bottom: 32px;
  `,
  intro_video: css`
    width: 100%;
    display: block;
    border-radius: 12px;
    background: ${colors.black};
  `,
  intro_placeholder: css`
    width: 100%;
    height: 160px;
    border-radius: 12px;
    background: ${colors.grayAnatomyLight5};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.grayAnatomyBase};
    font-size: ${fontSize.normal};
    text-align: center;
  `,
  intro_upload: css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  `,
  intro_upload_text: css`
    white-space: nowrap;
  `,
  input_error: css`
    border-color: ${colors.red};
  `,
  drag_disabled: css`
    cursor: not-allowed;
    color: ${colors.grayAnatomyLight2};
    opacity: 0.5;

    &:hover {
      color: ${colors.grayAnatomyLight2};
    }
  `,
  title_row: css`
    display: flex;
    gap: 12px;
    align-items: flex-start;
  `,
  title_input: css`
    flex: 1;
  `,
  time_limit_select: css`
    width: 120px;
    min-width: 120px;
    height: 42px;
    border-radius: 8px;
  `,
  header: css`
    display: flex;
    align-items: flex-start;
    gap: 24px;
  `,
  locked_banner: css`
    flex: 1;
    margin-top: 0 !important;
  `,
};

InterviewQuestionsList.propTypes = {
  interviewData: PropTypes.object,
  loading: PropTypes.bool,
};

SortableItem.propTypes = {
  question: PropTypes.object.isRequired,
  handleVideoUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool.isRequired,
  setQuestions: PropTypes.func.isRequired,
  questions: PropTypes.array.isRequired,
  isInterviewDisabled: PropTypes.bool.isRequired,
};

IntroVideoMedia.propTypes = {
  video: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  isInterviewDisabled: PropTypes.bool.isRequired,
};

export default InterviewQuestionsList;
